import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Chart from "react-apexcharts";
import Select, { components } from "react-select";
import {
  fetchResourceUtilFilters,
  fetchResourceUtilResult,
} from "../../../API_Service/Dashboard_Slice";
import { companyID, employeeID } from "../../../Common/UIComponents";

const COLORS = [
  "#008FFB",
  "#00E396",
  "#FEB019",
  "#FF4560",
  "#775DD0",
  "#546E7A",
  "#26a69a",
  "#D10CE8",
];

const ALL_OPTION = { value: "__ALL__", label: "Select All" };

/* ---- Collapse chips after the first one: "Facility A  +3 more" ---- */
const CollapsedMultiValue = ({ index, getValue, ...props }) => {
  const values = getValue();
  const maxToShow = 1;
  if (index < maxToShow) return <components.MultiValue {...props} />;
  return index === maxToShow ? (
    <div style={{ fontSize: 12, color: "#546E7A", marginLeft: 4 }}>
      +{values.length - maxToShow} more
    </div>
  ) : null;
};

/* ---- Compact styles so the control doesn't sprawl ---- */
const compactSelectStyles = {
  container: (base) => ({ ...base, width: 240 }),
  control: (base) => ({
    ...base,
    minHeight: 34,
    fontSize: 13,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 6px",
    flexWrap: "nowrap",
    overflow: "hidden",
  }),
  menu: (base) => ({ ...base, fontSize: 13, zIndex: 20 }),
};

/**
 * Multi-select with a "Select All" pseudo-option.
 * Fires onChange(ids: number[]) immediately on every change —
 * no separate Generate button needed.
 */
function MultiSelectWithAll({ options, value, onChange, placeholder }) {
  const allSelected = options.length > 0 && value.length === options.length;

  const selectOptions = [ALL_OPTION, ...options];
  const selectedValues = allSelected
    ? [ALL_OPTION, ...options]
    : options.filter((o) => value.includes(o.value));

  const handleChange = (selected, actionMeta) => {
    switch (actionMeta.action) {
      case "select-option":
        if (actionMeta.option?.value === ALL_OPTION.value) {
          onChange(options.map((o) => o.value));
          return;
        }
        break;
      case "deselect-option":
        if (actionMeta.option?.value === ALL_OPTION.value) {
          onChange([]);
          return;
        }
        break;
      case "remove-value":
        if (actionMeta.removedValue?.value === ALL_OPTION.value) {
          onChange([]);
          return;
        }
        break;
      case "clear":
        onChange([]);
        return;
      default:
        break;
    }
    onChange(
      (selected || [])
        .filter((s) => s.value !== ALL_OPTION.value)
        .map((s) => s.value),
    );
  };

  return (
    <Select
      isMulti
      options={selectOptions}
      value={selectedValues}
      onChange={handleChange}
      placeholder={placeholder}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      styles={compactSelectStyles}
      components={{ MultiValue: CollapsedMultiValue }}
    />
  );
}

const barOptions = (categories, columnWidth = "65%") => ({
  chart: { type: "bar", height: 350, toolbar: { show: true } },
  colors: COLORS,
  plotOptions: {
    bar: {
      columnWidth,
      horizontal: false,
      distributed: true,
      dataLabels: { position: "center" },
    },
  },
  dataLabels: { enabled: true },
  legend: { show: false },
  xaxis: {
    categories,
    labels: {
      style: { colors: categories.map(() => "#546E7A"), fontSize: "12px" },
    },
  },
});

const ChartPair = ({ hoursOptions, hoursSeries, projOptions, projSeries }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
    <div style={{ flex: "1 1 48%", minWidth: 300 }}>
      <h4 style={{ textAlign: "center" }}>
        <b>No of Hours</b>
      </h4>
      <Chart
        options={hoursOptions}
        series={hoursSeries}
        type="bar"
        height={350}
      />
    </div>
    <div style={{ flex: "1 1 48%", minWidth: 300 }}>
      <h4 style={{ textAlign: "center" }}>
        <b>No of Projects</b>
      </h4>
      <Chart
        options={projOptions}
        series={projSeries}
        type="bar"
        height={350}
      />
    </div>
  </div>
);

export default function ResourceUtilization() {
  const dispatch = useDispatch();

  const currentYear = new Date().getFullYear();
  const years = [
    { value: 0, label: "ALL" },
    ...Array.from({ length: currentYear - 2018 }, (_, i) => {
      const y = currentYear - i;
      return { value: y, label: String(y) };
    }),
  ];
  const months = [
    { value: 0, label: "MONTH" },
    ...Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: String(i + 1),
    })),
  ];

  const [filters, setFilters] = useState({
    facilities: [],
    testTypes: [],
    employees: [],
  });

  const [filterState, setFilterState] = useState({
    role: "TO",
    year: 0,
    month: 0,
    employeeId: employeeID,
    facilityIds: [],
    testTypeIds: [],
    type: "TESTTYPE",
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    dispatch(fetchResourceUtilFilters({ companyId: companyID }))
      .unwrap()
      .then((res) => {
        setFilters({
          facilities: res.facilities ?? [],
          testTypes: res.testTypes ?? [],
          employees: res.employees ?? [],
        });
      })
      .catch((err) => setError(err?.message || "Failed to load filters"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runReport = (override = {}) => {
    const f = { ...filterState, ...override };
    setLoading(true);
    dispatch(
      fetchResourceUtilResult({
        EmpId: f.employeeId,
        employeeid: f.employeeId,
        selTestOfficer: f.role,
        TestTypeid: f.testTypeIds.length ? f.testTypeIds.join(",") : "null",
        FacilityID: f.facilityIds.length ? f.facilityIds.join(",") : "null",
        BusinessUnitID: f.businessUnitIds?.length
          ? f.businessUnitIds.join(",")
          : "null",
        Year: f.year,
        Month: f.month,
        type: f.type,
        CompanyID: companyID,
      }),
    )
      .unwrap()
      .then((res) => setData(res))
      .catch((err) => setError(err?.message || "Failed to load chart data"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    runReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterState.role,
    filterState.year,
    filterState.month,
    filterState.employeeId,
    filterState.type,
  ]);

  /* Auto-fetch as soon as facility / test type selection changes */
  const handleFacilityChange = (ids) => {
    setFilterState((p) => ({ ...p, facilityIds: ids }));
    runReport({ facilityIds: ids });
  };

  const handleTestTypeChange = (ids) => {
    setFilterState((p) => ({ ...p, testTypeIds: ids }));
    runReport({ testTypeIds: ids });
  };

  const testTypeHours = useMemo(() => {
    const rows = data?.lstTesttype ?? [];
    return {
      categories: rows.map((r) => r.TestTypeNamess),
      series: [{ name: "TOTAL HOURS", data: rows.map((r) => r.TotHrs) }],
    };
  }, [data]);

  const testTypeProj = useMemo(() => {
    const rows = data?.lstProjCntTesttype ?? [];
    return {
      categories: rows.map((r) => r.TestTypeNamess),
      series: [{ name: "Project Count", data: rows.map((r) => r.NoofProj) }],
    };
  }, [data]);

  const facilityHours = useMemo(() => {
    const rows = data?.lstFacilityName ?? [];
    return {
      categories: rows.map((r) => r.Facilitys),
      series: [{ name: "Total Hours", data: rows.map((r) => r.TotHrs) }],
    };
  }, [data]);

  const facilityProj = useMemo(() => {
    const rows = data?.lstProjCntFacilityName ?? [];
    return {
      categories: rows.map((r) => r.Facilitys),
      series: [{ name: "Project Count", data: rows.map((r) => r.NoofProj) }],
    };
  }, [data]);

  return (
    <div className="panel-body">
      <header
        className="panel-heading"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2 className="panel-title">Resource Utilization</h2>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={filterState.type === "TESTTYPE"}
            onChange={(e) =>
              setFilterState((p) => ({
                ...p,
                type: e.target.checked ? "TESTTYPE" : "ASSOCIATE",
              }))
            }
          />
          {filterState.type === "TESTTYPE"
            ? "Test Type wise"
            : "Associate wise"}
        </label>
      </header>

      <div
        className="form-group"
        style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}
      >
        <select
          className="form-control"
          style={{ width: 120 }}
          value={filterState.role}
          onChange={(e) =>
            setFilterState((p) => ({ ...p, role: e.target.value }))
          }
        >
          <option value="TO">TO</option>
          <option value="TO1">TO1</option>
          <option value="PR">PR</option>
        </select>

        <select
          className="form-control"
          style={{ width: 100 }}
          value={filterState.year}
          onChange={(e) =>
            setFilterState((p) => ({ ...p, year: Number(e.target.value) }))
          }
        >
          {years.map((y) => (
            <option key={y.value} value={y.value}>
              {y.label}
            </option>
          ))}
        </select>

        <select
          className="form-control"
          style={{ width: 110 }}
          value={filterState.month}
          onChange={(e) =>
            setFilterState((p) => ({ ...p, month: Number(e.target.value) }))
          }
        >
          {months.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>

        {filterState.type === "TESTTYPE" && (
          <select
            className="form-control"
            style={{ width: 220 }}
            value={filterState.employeeId}
            onChange={(e) =>
              setFilterState((p) => ({
                ...p,
                employeeId: Number(e.target.value),
              }))
            }
          >
            <option value={0}>Select Employee</option>
            {filters.employees.map((emp) => (
              <option key={emp.Value} value={emp.Value}>
                {emp.Text}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && <p style={{ color: "#e74c3c" }}>{error}</p>}
      {loading && <p>Loading…</p>}

      {/* Facility section */}
      <div
        className="card"
        style={{
          padding: 16,
          marginBottom: 16,
          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ marginBottom: 8 }}>
          <MultiSelectWithAll
            options={filters.facilities.map((f) => ({
              value: f.Value,
              label: f.Text,
            }))}
            value={filterState.facilityIds}
            onChange={handleFacilityChange}
            placeholder="Select Facility"
          />
        </div>
        <ChartPair
          hoursOptions={barOptions(facilityHours.categories)}
          hoursSeries={facilityHours.series}
          projOptions={barOptions(facilityProj.categories)}
          projSeries={facilityProj.series}
        />
      </div>

      {/* Test Type section */}
      <div
        className="card"
        style={{ padding: 16, boxShadow: "0 4px 8px rgba(0,0,0,0.2)" }}
      >
        <div style={{ marginBottom: 8 }}>
          <MultiSelectWithAll
            options={filters.testTypes.map((t) => ({
              value: t.Value,
              label: t.Text,
            }))}
            value={filterState.testTypeIds}
            onChange={handleTestTypeChange}
            placeholder="Select Test Type"
          />
        </div>
        <ChartPair
          hoursOptions={barOptions(testTypeHours.categories)}
          hoursSeries={testTypeHours.series}
          projOptions={barOptions(testTypeProj.categories)}
          projSeries={testTypeProj.series}
        />
      </div>
    </div>
  );
}
